import { Component, effect, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TruncateDirective } from 'shared';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { AvatarComponent } from '../avatar/avatar.component';
import { SidebarListComponent } from '../sidebar-list/sidebar-list.component';
import type {
  SidebarListFooterAction,
  SidebarListItem,
} from '../sidebar-list/sidebar-list.interfaces';
import type { ContextNavDropdownItem, ContextNavItem } from './context-nav.interfaces';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { ContextNavViewStore } from './context-nav.store';

@Component({
  selector: 'ocp-context-nav',
  imports: [
    HlmPopoverImports,
    SidebarListComponent,
    AvatarComponent,
    TranslatePipe,
    TruncateDirective,
    ThemedIconComponent,
  ],
  templateUrl: './context-nav.component.html',
  styleUrl: './context-nav.component.scss',
  providers: [ContextNavViewStore],
})
export class ContextNavComponent {
  private readonly store = inject(ContextNavViewStore);

  readonly items = input<ContextNavItem[]>([]);
  readonly showTriggerAvatar = input(true);
  readonly showCreate = input(false);
  readonly showCreateInHeader = input(false);
  readonly itemSelected = output<{ contextItemId: string; dropdownItem: ContextNavDropdownItem }>();
  readonly createClicked = output<string>();
  readonly footerActionClicked = output<{
    contextItemId: string;
    action: SidebarListFooterAction;
  }>();

  readonly activeItem = this.store.activeItem;
  readonly viewItems = this.store.viewItems;
  readonly activePopoverItem = this.store.activePopoverItem;

  protected readonly isOpen = signal(false);
  protected readonly overlayState = signal<'open' | 'closed' | null>(null);
  /** Tracks the clicked button so the popover anchors to it */
  protected readonly activeTrigger = signal<HTMLElement | null>(null);

  constructor() {
    effect(() => this.store.items.set(this.items()));
    effect(() => this.store.showTriggerAvatar.set(this.showTriggerAvatar()));
  }

  private closePopover(): void {
    this.overlayState.set('closed');
  }

  protected onStateChanged(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.overlayState.set(null);
      this.store.setActiveItem(null);
    }
    this.isOpen.set(state === 'open');
  }

  protected onTriggerToggle(event: Event, item: ContextNavItem): void {
    this.store.setActiveItem(item);
    if (this.isOpen()) {
      this.closePopover();
    } else {
      this.activeTrigger.set(event.currentTarget as HTMLElement);
      this.overlayState.set('open');
    }
  }

  protected onSidebarListItemClick(item: SidebarListItem): void {
    const active = this.store.activeItem();
    if (active) {
      this.itemSelected.emit({
        contextItemId: active.id,
        dropdownItem: item as ContextNavDropdownItem,
      });
    }
    this.closePopover();
  }

  protected onCreateClick(contextItemId: string): void {
    this.createClicked.emit(contextItemId);
    this.closePopover();
  }

  protected onFooterActionClick(contextItemId: string, action: SidebarListFooterAction): void {
    this.footerActionClicked.emit({ contextItemId, action });
    this.closePopover();
  }
}
